import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, FolderKanban, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { projectsApi, type Project } from "@/lib/projects-api";
import { useScope } from "@/lib/scope";

// Top of sidebar: pick a project inside the active workspace. List + create.
export function ProjectSwitcher() {
  const { workspaceId, projectId, setProjectId } = useScope();
  const qc = useQueryClient();

  const projects = useQuery({
    queryKey: ["projects", workspaceId],
    queryFn: () => projectsApi.list(),
    enabled: !!workspaceId,
  });

  const current = projects.data?.find((p: Project) => p.id === projectId);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const create = useMutation({
    mutationFn: () => projectsApi.create({ name: newName }),
    onSuccess: (project) => {
      qc.invalidateQueries({ queryKey: ["projects", workspaceId] });
      setProjectId(project.id);
      setNewName("");
      setCreateOpen(false);
    },
  });

  return (
    <div className="px-3 pt-3 pb-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md border bg-background px-2 py-2 text-left text-sm transition-colors hover:bg-muted"
          >
            <FolderKanban className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">
              {current?.name ?? "Select a project"}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-60">
          <DropdownMenuLabel className="text-xs uppercase text-muted-foreground">
            Projects
          </DropdownMenuLabel>
          {projects.data?.length === 0 && (
            <div className="px-2 py-3 text-xs text-muted-foreground">
              No projects yet.
            </div>
          )}
          {projects.data?.map((p: Project) => (
            <DropdownMenuItem
              key={p.id}
              onSelect={() => setProjectId(p.id)}
              className="flex items-center justify-between"
            >
              <span className="truncate">{p.name}</span>
              {p.id === projectId && <Check className="h-4 w-4" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New project
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New project</DialogTitle>
            <DialogDescription>
              Projects own their own credentials, loadouts and memory. They
              live inside the active workspace.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="new-project-name">Name</Label>
            <Input
              id="new-project-name"
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="My Project"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newName.trim()) create.mutate();
              }}
            />
            {create.error && (
              <p className="text-sm text-destructive">
                {(create.error as Error).message}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => create.mutate()}
              disabled={!newName.trim() || create.isPending}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
